import {
  Component,
  OnInit,
  inject
} from '@angular/core';

import {
  Router,
  RouterLink,
  RouterLinkActive,
  NavigationEnd
} from '@angular/router';

import { CommonModule } from '@angular/common';
import { KeycloakService } from 'keycloak-angular';
import { filter } from 'rxjs/operators';

import { SidebarService } from '../services/sidebar.service';
import { PermissionService } from '../services/permission.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-sidebar',
  imports: [
    RouterLink,
    RouterLinkActive,
    CommonModule,
    TranslatePipe
  ],
  templateUrl: './sidebar.html',
})
export class Sidebar implements OnInit {

  private keycloakService = inject(KeycloakService);
  private router = inject(Router);

  permissionService = inject(PermissionService);

  isGLOpen = false;
  isOpen = false;
  username = '';

  isSetupOpen = false;
  isAdminOpen = false;
  isReportsOpen = false;
  isCreditOpen = false;
  isMonitoringOpen = false;
  isDisbursementOpen = false;

  private creditRoutes: string[] = [
    '/cradit-list',
  ];

  private disbursementRoutes: string[] = [
    '/disbursement',
    '/proposed-disbursement',
    '/disbursement-note',
  ];

  private monitoringRoutes: string[] = [
    '/monitoring',
  ];

  private setupRoutes: string[] = [
    '/common-codes',
    '/kibor',
    '/city',
    '/geography',
    '/questioner',
    '/document-setup',
    '/financial',
    '/income-statement-type',
    '/client-gl-code',
    '/payment-type',
    '/user-management',
  ];

  private glRoutes: string[] = [
    '/client-gl-code',
    '/payment-type',
  ];

  private adminRoutes: string[] = [
    '/general-voucher',
    '/role-assignment',
    '/user-management',
  ];

  constructor(
    public sidebarService: SidebarService
  ) {}

  ngOnInit(): void {

    this.sidebarService.isOpen$.subscribe((value) => {
      this.isOpen = value;
    });

    this.setOpenMenusByCurrentRoute();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.setOpenMenusByCurrentRoute();
      });

    const keycloak = this.keycloakService.getKeycloakInstance();

    this.username =
      keycloak.tokenParsed?.['preferred_username'] || '';
  }

  private setOpenMenusByCurrentRoute(): void {

    const currentUrl = this.router.url.split('?')[0];

    this.isSetupOpen =
      this.setupRoutes.some((route) =>
        currentUrl.startsWith(route)
      );

    this.isGLOpen =
      this.glRoutes.some((route) =>
        currentUrl.startsWith(route)
      );

    this.isAdminOpen =
      this.adminRoutes.some((route) =>
        currentUrl.startsWith(route)
      );

    this.isReportsOpen =
      currentUrl.startsWith('/reports');

    this.isCreditOpen =
      this.creditRoutes.some((route) =>
        currentUrl.startsWith(route)
      );

    this.isMonitoringOpen =
      this.monitoringRoutes.some((route) =>
        currentUrl.startsWith(route)
      );

    this.isDisbursementOpen =
      this.disbursementRoutes.some((route) =>
        currentUrl.startsWith(route)
      );
  }

  hasAccess(route: string): boolean {
    return this.permissionService.hasAccess(route);
  }

  hasAnyAccess(routes: string[]): boolean {
    return this.permissionService.hasAnyAccess(routes);
  }

  /**
   * CNIC Expiry is worked by the Relationship Officer (submit) and the Credit
   * Manager (approve), so it shows for those roles even before the module is
   * assigned to them in role permissions.
   */
  get canSeeCnicExpiry(): boolean {
    const role = (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
      .trim()
      .toUpperCase();

    return this.hasAccess('/cnic-expiry') || role === 'LNLY_RO' || role === 'LNLY_CM';
  }

  toggleSetup(): void {
    this.isSetupOpen = !this.isSetupOpen;
  }

  toggleAdmin(): void {
    this.isAdminOpen = !this.isAdminOpen;
  }

  toggleReports(): void {
    this.isReportsOpen = !this.isReportsOpen;
  }

  toggleCredit(): void {
    this.isCreditOpen = !this.isCreditOpen;
  }

  toggleMonitoring(): void {
    this.isMonitoringOpen = !this.isMonitoringOpen;
  }

  toggleDisbursement(): void {
    this.isDisbursementOpen = !this.isDisbursementOpen;
  }

  async logout(): Promise<void> {

    this.permissionService.clearPermissions();

    await this.clearBrowserAppData();

    const keycloak = this.keycloakService.getKeycloakInstance();

    await keycloak.logout({
      redirectUri: window.location.origin
    });
  }

  private async clearBrowserAppData(): Promise<void> {

    try {
      localStorage.clear();
    } catch (error) {
      console.error('Unable to clear localStorage:', error);
    }

    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Unable to clear sessionStorage:', error);
    }

    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();

        await Promise.all(
          cacheNames.map((cacheName) =>
            caches.delete(cacheName)
          )
        );
      }
    } catch (error) {
      console.error('Unable to clear Cache Storage:', error);
    }
  }
}